"use client";

import { useEffect, useRef, useState } from "react";
import {
  Card,
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Backdrop,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  FormHelperText,
} from "@mui/material";
import {
  Link as LinkIcon,
  Description as FileTextIcon,
  Work as WorkIcon,
} from "@mui/icons-material";
import { GetdriveFilesHelper } from "../../helper/AtsResumeParserHelper";
import { useDispatch, useSelector } from "react-redux";
import { GetCandidateSourceAction } from "../../../../../store/ATS/CandidateSource/Action";
import { LabelWithInfo } from "../../../Base/views/LabelwithInfo";
import { makeStyles } from "@mui/styles";
import AICongifWarningPopup from "../../helper/Ancillary/AICongifWarningPopup";

const useStyles = makeStyles((theme) => ({
    Backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      backgroundColor: "rgba(255, 255, 255, 0.5)",
      position: "absolute",
      borderRadius: "inherit",
      backdropFilter: "blur(2px)",
      WebkitBackdropFilter: 'blur(2px)',
    },
  }));
const DrivefileProcessor = ({job_id}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [link, setLink] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const aiConfigPopupRef = useRef();
  const { AiConfigLoading, ModelTestingLoading} = useSelector(
      (state) => state.AncillaryReducer?.AIConfigReducer
    );

  const { candidateSourceData } = useSelector((state) => state.AtsReducer?.CandidateSourceReducer || {});
  const candidateSourceList = Array.isArray(candidateSourceData) ? candidateSourceData?.filter((el) => !el?.is_default) : [];
  const [selectedSource, setSelectedSource] = useState("");
  const [sourceError, setSourceError] = useState("");

  useEffect(() => {
    dispatch(GetCandidateSourceAction());
  }, [dispatch]);

  // 🔹 Validate Google Drive link
  const isValidGoogleDriveLink = (url) =>
    url.includes("drive.google.com") || url.includes("docs.google.com");

  // 🔹 Extract File or Folder ID
  const extractDriveId = (url) => {
    const fileMatch = url.match(/\/(?:file|document|spreadsheets|presentation)\/d\/([^/?]+)/);
    const folderMatch = url.match(/\/folders\/([^/?]+)/);
    return {
      id: fileMatch ? fileMatch[1] : folderMatch ? folderMatch[1] : null,
      type: fileMatch ? "file" : folderMatch ? "folder" : null,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!link.trim()) return;

    setError("");
    if (!isValidGoogleDriveLink(link)) {
      setError("Please enter a valid Google Drive link");
      return;
    }

    if (!selectedSource) {
      setSourceError("Please select a candidate source");
      return;
    }

    setIsLoading(true);
    const { id, type } = extractDriveId(link);
    if (!id) {
      setError("Invalid Google Drive link");
      setIsLoading(false);
      return;
    }

    try {
       await GetdriveFilesHelper({
         fileId: id,
         type,
         job_id,
         source_id: (selectedSource && selectedSource !== "General") ? selectedSource : null
       });
       setLink("");
       setSelectedSource("");
    } finally {
      setIsLoading(false);
    }
  };

  // Trigger model_validation 
  useEffect(() => {
    if (aiConfigPopupRef.current) {
      aiConfigPopupRef.current.validateAIConfig();
    }
  }, []);
  return (
    <Box sx={{ position: "relative", width: "100%",pt: 1, pb: 1, px: 0.5}}>
      <Backdrop className={classes.Backdrop} open={AiConfigLoading || ModelTestingLoading}>
        <CircularProgress />
      </Backdrop>
      <Card
        sx={{
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
          borderRadius: 2,
        }}
      >
        <Grid container sx={{ minHeight: 400 }}>
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              background: "linear-gradient(135deg, #3b82f6 0%, #7c3aed 50%, #ec4899 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 4,
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Decorative Blobs */}
            <Box
              sx={{
                position: "absolute",
                top: 40,
                left: 40,
                width: 80,
                height: 80,
                background: "rgba(255, 255, 255, 0.1)",
                borderRadius: "50%",
                filter: "blur(40px)",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: 80,
                right: 40,
                width: 128,
                height: 128,
                background: "rgba(255, 255, 255, 0.1)",
                borderRadius: "50%",
                filter: "blur(80px)",
              }}
            />

            {/* Animated Icons */}
            <Box sx={{ position: "relative", zIndex: 10, textAlign: "center" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 64,
                    height: 64,
                    background: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                    borderRadius: "50%",
                    animation: "bounce 2s infinite",
                    "@keyframes bounce": {
                      "0%, 100%": { transform: "translateY(0)" },
                      "50%": { transform: "translateY(-10px)" },
                    },
                  }}
                >
                  <FileTextIcon size={32} color="white" />
                </Box>
                <Typography sx={{ color: "rgba(255, 255, 255, 0.6)", fontSize: 24 }}>→</Typography>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 64,
                    height: 64,
                    background: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                    borderRadius: "50%",
                    animation: "bounce 2s infinite 0.2s",
                    "@keyframes bounce": {
                      "0%, 100%": { transform: "translateY(0)" },
                      "50%": { transform: "translateY(-10px)" },
                    },
                  }}
                >
                  <WorkIcon size={32} color="white" />
                </Box>
              </Box>
              <Typography sx={{ color: "white", fontSize: 20, fontWeight: "bold", mb: 1 }}>
                Resume to Candidate
              </Typography>
              <Typography sx={{ color: "rgba(255, 255, 255, 0.8)", fontSize: 14 }}>
                Parse resumes and propose candidates
              </Typography>
            </Box>
          </Grid>

          {/* Right Side - Form */}
          <Grid
            item
            xs={12}
            md={7}
            sx={{
              px: { xs: 3, md: 4},
              py: { xs: 3, md: 2},
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" sx={{ fontWeight: "bold", color: "#1e293b", mb: 1 }}>
                Upload Resume
              </Typography>
              <Typography sx={{ color: "#64748b", fontSize: 14 }}>
                Share your Google Drive resume link to parse and propose for job opportunities
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                type="url"
                placeholder="https://drive.google.com/file/d/... or folder link"
                value={link}
                onChange={(e) =>{ setLink(e.target.value); setError("")}}
                disabled={isLoading}
                fullWidth
                variant="outlined"
                size="medium"
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, display: "flex", alignItems: "center", color: "#94a3b8" }}>
                      <LinkIcon size={20} />
                    </Box>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: 16,
                    "& fieldset": { borderColor: "#e2e8f0", borderWidth: 2 },
                    "&:hover fieldset": { borderColor: "#cbd5e1" },
                    "&.Mui-focused fieldset": { borderColor: "#7c3aed", borderWidth: 2 },
                    "& input::placeholder": {
                            fontSize: "16px",
                            color: "#aaa",
                          },
                  },
                }}
              />

              {error && <Alert severity="error" sx={{ fontSize: 14 }}>{error}</Alert>}

              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} sm={6}>
                  <FormControl
                    fullWidth
                    size="medium"
                    error={Boolean(sourceError)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "56px",
                        "& fieldset": { borderColor: "#e2e8f0", borderWidth: 2 },
                        "&:hover fieldset": { borderColor: "#cbd5e1" },
                        "&.Mui-focused fieldset": { borderColor: "#7c3aed", borderWidth: 2 },
                      }
                    }}
                  >
                    <InputLabel id="drive-candidate-source-select-label">Select Candidate Source *</InputLabel>
                    <Select
                      labelId="drive-candidate-source-select-label"
                      id="drive-candidate-source-select"
                      value={selectedSource}
                      label="Select Candidate Source *"
                      onChange={(e) => {
                        setSelectedSource(e.target.value);
                        setSourceError("");
                      }}
                      disabled={isLoading}
                    >
                      <ListSubheader
                        disableSticky
                        style={{
                          lineHeight: "26px",
                          backgroundColor: "#f9fafb",
                          fontSize: "12px",
                          color: "#6b7280",
                          fontWeight: 600,
                          cursor: "default"
                        }}
                      >
                        <LabelWithInfo
                          label="Select Candidate Source"
                          tooltipContent="Select the candidate source where these resumes were sourced from"
                          mandatory
                        />
                      </ListSubheader>
                      <MenuItem value="General">
                        <em>General (No Source)</em>
                      </MenuItem>
                      {candidateSourceList?.map((source) => (
                        <MenuItem
                          key={source.candidate_source_configuration_pk}
                          value={source.candidate_source_configuration_pk}
                        >
                          {source.source_name}
                        </MenuItem>
                      ))}
                    </Select>
                    {sourceError && (
                      <FormHelperText>{sourceError}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    type="submit"
                    disabled={!link.trim() || isLoading || !isValidGoogleDriveLink(link)}
                    fullWidth
                    variant="contained"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "56px",
                      padding: "10px 20px",
                      backgroundColor: "#8b44ff",
                      color: "#fff",
                      borderRadius: "5px",
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: 16,
                      textTransform: "none",
                      "&:disabled": {
                        opacity: 0.5,
                        cursor: "not-allowed",
                      },
                      "&:hover": {
                        backgroundColor: "#7a3ce0",
                      },
                    }}
                  >
                    {isLoading ? (
                      <LoadingMessages />
                    ) : (
                      "Parse & Upload"
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {/* Helper steps */}
            <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #e2e8f0" }}>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#64748b",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  mb: 2,
                }}
              >
                How to share your resume:
              </Typography>
              <List sx={{ p: 0 }}>
                {[
                  "Upload resumes to Google Drive",
                  'Right-click and select "Share" with Anyone with this link access',
                  "Copy the shareable link and paste it above",
                ].map((step, index) => (
                  <ListItem key={index} sx={{ p: 0, mb: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32, color: "#7c3aed" }}>
                      <Typography sx={{ fontWeight: "bold", fontSize: 14 }}>{index + 1}.</Typography>
                    </ListItemIcon>
                    <ListItemText primary={step} primaryTypographyProps={{ fontSize: 14, color: "#64748b" }} />
                  </ListItem>
                ))}
              </List>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#64748b",
                  letterSpacing: 0.5,
                  mb: 2,
                }}
              >
                <strong>Note:</strong> Only .pdf and .docx files are accepted.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Card>
      <AICongifWarningPopup ref={aiConfigPopupRef} validateFor="Resume" />
    </Box>
  );
};
const LoadingMessages = () => {
  const messages = [
    "Accessing files from drive...",
    "Reading file metadata...",
    "Parsing resumes...",
    "Extracting candidate details...",
  ];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % messages.length);
    }, 2000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, padding: "5px 15px", }}>
      <CircularProgress size={20} sx={{ color: "white" }} />
      <Typography sx={{ fontSize: 14 }}>{messages[index]}</Typography>
    </Box>
  );
};

export default DrivefileProcessor;
    